"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  ROUNDING_MODES,
  type RoundingMode,
} from "@/lib/tool-kit/arithmetic/rounding";
import {
  computeRounding,
  type RoundingComputation,
  type RoundingView,
  roundingLines,
} from "@/lib/tool-kit/arithmetic/rounding-view";

/** English strings. A translated content file overrides these through `ui`. */
export const ROUNDING_CALCULATOR_STRINGS = {
  valueLabel: "Number to round",
  valueHint:
    "Any number, with up to 120 digits. Digits are kept exactly as typed.",
  targetLabel: "Round to",
  targetPlace: "A decimal place",
  targetIncrement: "A multiple of",
  placeLabel: "Round to the",
  incrementLabel: "Nearest multiple of",
  incrementHint: "For example 0.05, 5 or 0.25.",
  modeLabel: "Rounding mode",
  nearest: "Nearest {unit}",
  decimalPlaces: "one: {count} decimal place|other: {count} decimal places",
  units: {
    "-3": "thousand",
    "-2": "hundred",
    "-1": "ten",
    "0": "whole number",
    "1": "tenth",
    "2": "hundredth",
    "3": "thousandth",
    "4": "ten-thousandth",
    "5": "hundred-thousandth",
    "6": "millionth",
  },
  modeNames: {
    "half-away": "Half away from zero (school rounding)",
    "half-up": "Half up, toward +∞",
    "half-down": "Half down, toward −∞",
    "half-zero": "Half toward zero",
    "half-even": "Half to even (banker’s rounding)",
    "half-odd": "Half to odd",
    ceiling: "Ceiling, toward +∞",
    floor: "Floor, toward −∞",
    truncate: "Truncate, toward zero",
    away: "Away from zero",
  },
  modeRules: {
    "half-away":
      "A tie goes to the larger magnitude. Excel ROUND, Java HALF_UP and Python’s decimal ROUND_HALF_UP.",
    "half-up":
      "A tie goes toward +∞, so −2.5 becomes −2. JavaScript’s Math.round.",
    "half-down": "A tie goes toward −∞, so 2.5 becomes 2 and −2.5 becomes −3.",
    "half-zero": "A tie goes to the smaller magnitude. Java HALF_DOWN.",
    "half-even":
      "A tie goes to the neighbour whose last digit is even. IEEE 754’s default, Python’s round() and NIST’s rule.",
    "half-odd": "A tie goes to the neighbour whose last digit is odd.",
    ceiling: "Always up, toward +∞, whatever the dropped digits.",
    floor: "Always down, toward −∞, whatever the dropped digits.",
    truncate: "The dropped digits are simply cut off.",
    away: "Up in magnitude whenever any non-zero digit is dropped.",
  },
  results: "Results",
  result: "Result",
  roundedFrom: "{value} rounded",
  change: "Change from the original",
  tieStat: "Exactly halfway?",
  yes: "Yes",
  no: "No",
  copy: "Copy result",
  copied: "Copied!",
  workingHeading: "The working",
  keep: "Keep {kept}. The digits that follow are {dropped}.",
  below:
    "{dropped} is less than half a unit in the {unit} place (half would be {half}), so the dropped part is below the halfway point.",
  tie: "{dropped} is exactly half a unit in the {unit} place: a tie. The rounding mode decides which neighbour wins.",
  above:
    "{dropped} is more than half a unit in the {unit} place (half would be {half}), so it is past the halfway point.",
  exact: "Nothing non-zero follows, so the number is already exact here.",
  stays: "The last kept digit stays the same: {result}.",
  bumps: "The last kept digit goes up by one: {result}.",
  unchanged:
    "{value} has no digits beyond the {unit} place, so rounding leaves it unchanged.",
  incrementRatio:
    "{value} ÷ {step} = {ratio}, which sits between two whole multiples.",
  incrementBelow:
    "The fractional part is below one half, so the nearest multiple is below.",
  incrementTie:
    "The fractional part is exactly one half: a tie that the mode decides.",
  incrementAbove:
    "The fractional part is above one half, so the nearest multiple is above.",
  incrementExact: "It is already an exact multiple.",
  incrementResult: "{multiple} × {step} = {result}.",
  compareHeading: "Every rounding mode on your number",
  columnMode: "Mode",
  columnResult: "Result",
  columnRule: "Rule",
  allSame:
    "All ten modes agree here because the dropped part is not a tie and the number is not exactly on a multiple. They disagree on ties and on directed rounding.",
  floatingHeading: "Why a calculator or spreadsheet can disagree",
  floatingNote:
    "In binary floating point, {value} is stored as {exact}. JavaScript’s toFixed({places}) therefore returns {toFixed}, while rounding the decimal digits you typed gives {result}.",
  enterNumber: "Enter a number to round it.",
  invalid: "That is not a number. Try 2.675, −7.5 or 1234.5678.",
  invalidIncrement: "The multiple must be a positive number such as 0.05 or 5.",
} as const;

type Strings = typeof ROUNDING_CALCULATOR_STRINGS;
type TargetKind = "place" | "increment";

const PLACE_CHOICES = [-3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 8, 10];

function unitFor(places: number, strings: Strings, locale: string): string {
  const key = String(places) as keyof Strings["units"];
  const named = strings.units[key];
  if (named) {
    return named;
  }
  return tPlural(strings.decimalPlaces, locale, places).trim();
}

function placeOption(places: number, strings: Strings, locale: string): string {
  const key = String(places) as keyof Strings["units"];
  if (strings.units[key]) {
    return t(strings.nearest, { unit: strings.units[key] });
  }
  return tPlural(strings.decimalPlaces, locale, places).trim();
}

function ComparisonTable({
  view,
  strings,
}: {
  view: RoundingView;
  strings: Strings;
}) {
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <DataTable
        head={[strings.columnMode, strings.columnResult, strings.columnRule]}
        rows={view.all.map((row) => [
          strings.modeNames[row.mode],
          row.text,
          strings.modeRules[row.mode],
        ])}
      />
      {view.modesDiffer ? null : <Notice>{strings.allSame}</Notice>}
    </>
  );
}

function Results({
  view,
  strings,
  unit,
}: {
  view: RoundingView;
  strings: Strings;
  unit: string;
}) {
  const lines = roundingLines(view, strings, unit);
  const places = view.target.kind === "place" ? view.target.places : 0;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.roundedFrom, { value: view.valueText })}
          value={view.resultText}
        />
        <StatCard label={strings.change} value={view.differenceText} />
        <StatCard
          label={strings.tieStat}
          value={view.relation === "tie" ? strings.yes : strings.no}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={view.resultText}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {view.floating?.differs ? (
        <>
          <h3 className={WIDGET_H3}>{strings.floatingHeading}</h3>
          <Notice>
            {t(strings.floatingNote, {
              value: view.valueText,
              exact: view.floating.exactBinary,
              places,
              toFixed: view.floating.toFixed,
              result: view.all[0].text,
            })}
          </Notice>
        </>
      ) : null}
      <ComparisonTable strings={strings} view={view} />
    </>
  );
}

function Outcome({
  outcome,
  strings,
  unit,
}: {
  outcome: RoundingComputation;
  strings: Strings;
  unit: string;
}) {
  if (outcome.kind === "empty") {
    return <Notice>{strings.enterNumber}</Notice>;
  }
  if (outcome.kind === "invalid") {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  if (outcome.kind === "invalid-increment") {
    return <ErrorNotice>{strings.invalidIncrement}</ErrorNotice>;
  }
  return <Results strings={strings} unit={unit} view={outcome.view} />;
}

export default function RoundingCalculatorWidget() {
  const strings = useToolUi(ROUNDING_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const [value, setValue] = useState("2.675");
  const [kind, setKind] = useState<TargetKind>("place");
  const [places, setPlaces] = useState("2");
  const [step, setStep] = useState("0.05");
  const [mode, setMode] = useState<RoundingMode>("half-away");
  const placeCount = Number(places);
  const outcome = useMemo(
    () =>
      computeRounding(
        value,
        mode,
        kind === "place"
          ? { kind: "place", places: placeCount }
          : { kind: "increment", raw: step }
      ),
    [value, mode, kind, placeCount, step]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.valueHint}
          id="rc-value"
          label={strings.valueLabel}
          mono
          numeric
          onChange={setValue}
          value={value}
        />
        <PillGroup
          label={strings.targetLabel}
          name="rc-target"
          onChange={setKind}
          options={[
            { value: "place", label: strings.targetPlace },
            { value: "increment", label: strings.targetIncrement },
          ]}
          value={kind}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {kind === "place" ? (
          <SelectField
            id="rc-place"
            label={strings.placeLabel}
            onChange={setPlaces}
            options={PLACE_CHOICES.map((n) => ({
              value: String(n),
              label: placeOption(n, strings, locale),
            }))}
            value={places}
          />
        ) : (
          <TextField
            hint={strings.incrementHint}
            id="rc-step"
            label={strings.incrementLabel}
            mono
            numeric
            onChange={setStep}
            value={step}
          />
        )}
        <SelectField
          id="rc-mode"
          label={strings.modeLabel}
          onChange={setMode}
          options={ROUNDING_MODES.map((m) => ({
            value: m,
            label: strings.modeNames[m],
          }))}
          value={mode}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          outcome={outcome}
          strings={strings}
          unit={unitFor(placeCount, strings, locale)}
        />
      </section>
    </>
  );
}
